// Support desk — frontend API surface (/api/support-desk, server/routes.js).

import { client } from '@platform/http/api-client';

const enc = encodeURIComponent;

const supportDeskApi = {
  // { spaceIds: [...] } — every project that is a desk right now.
  async deskList() {
    return (await client.get('/api/support-desk/projects')).data;
  },
  // { spaceId, enabled } — answers for EVERY project, marked or not.
  async deskGet(spaceId) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}`)).data;
  },
  // { enabled: true | false } — admin only.
  async deskSet(spaceId, enabled) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}`, { enabled })).data;
  },
  // The desk's own sign-up: { enabled?, group?, domains? } — admin only.
  // Answers the desk, its signUp included.
  async deskSignUpSave(spaceId, patch) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/signup`, patch)).data;
  },
  // The desk's notifications — admin only. { staffEmails: [...], customer:
  // { statusChanges, statuses: [...], fieldChanges, publicComments } }.
  async deskNotifyGet(spaceId) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}/notifications`)).data;
  },
  // PATCH-shaped { staffEmails?, customer? } — answers the saved settings.
  async deskNotifySave(spaceId, patch) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/notifications`, patch)).data;
  },
  // Every group's name, for the sign-up's group picker.
  async groupNames() {
    const r = await client.get('/api/groups');
    return (r.data?.groups || []).map((g) => g.name || g.group_name).filter(Boolean);
  },

  // --- request types: the portal's forms ---

  // { requestTypes: [...] } — an admin gets the switched-off ones too.
  async typeList(spaceId) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}/request-types`)).data;
  },
  // { name, description?, workTypeId, fields? }
  async typeCreate(spaceId, payload) {
    return (await client.post(`/api/support-desk/projects/${enc(spaceId)}/request-types`, payload)).data;
  },
  // PATCH-shaped: only the keys you send are written.
  async typeUpdate(spaceId, id, payload) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/request-types/${enc(id)}`, payload)).data;
  },
  // { name, workTypeId, fields } — what Stride's create screen needs to raise
  // a ticket as this form would (staff).
  async typeCreatePresets(spaceId, id) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}/request-types/${enc(id)}/create-presets`)).data;
  },
  // The order the forms show in on the portal: every id, in the new order.
  async typeOrder(spaceId, ids) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/request-types/order`, { ids })).data;
  },
  async typeDelete(spaceId, id) {
    return (await client.delete(`/api/support-desk/projects/${enc(spaceId)}/request-types/${enc(id)}`)).data;
  },
  // --- the customer portal ---

  // { requestTypes: [...] } — every form this caller may raise, already
  // permission-filtered: a form you cannot submit is not on the page.
  async portalList() {
    return (await client.get('/api/portal/request-types')).data;
  },
  // { pages, site, staff } — the custom pages an admin opened to the
  // portal that this reader may view, with the site's mark for the frame.
  async portalPages() {
    return (await client.get('/api/portal/pages')).data;
  },
  // One form, resolved against the create screen.
  async portalForm(spaceId, requestTypeId) {
    return (await client.get(
      `/api/portal/request-types/${enc(spaceId)}/${enc(requestTypeId)}`)).data;
  },
  // { spaceId, requestTypeId, values } — the server keeps only the values
  // belonging to that form and chooses the project and work type itself.
  async portalRaise(payload) {
    return (await client.post('/api/portal/requests', payload)).data;
  },

  // { requests: [...] } — the caller's own requests, nobody else's.
  async portalMyRequests() {
    return (await client.get('/api/portal/requests')).data;
  },
  // One request: what they filled in, where it is, and the thread they may
  // read. Internal notes never reach here.
  async portalRequest(key) {
    return (await client.get(`/api/portal/requests/${enc(key)}`)).data;
  },
  async portalReply(key, text) {
    return (await client.post(`/api/portal/requests/${enc(key)}/comment`, { text })).data;
  },

  // A file on the caller's OWN request. Multipart, like every other upload
  // in Stride — the portal adds a door, not a second upload path.
  async portalAttach(key, file) {
    const formData = new FormData();
    formData.append('file', file);
    return (await client.post(`/api/portal/requests/${enc(key)}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  // A file the caller added to their OWN request, as a Blob — fetched with
  // the session (a plain link would carry no sign-in) and saved by the page.
  async portalAttachmentBlob(key, attachmentId) {
    return (await client.get(`/api/portal/requests/${enc(key)}/attachments/${enc(attachmentId)}`,
      { responseType: 'blob' })).data;
  },
  // Its preview (an image, a PDF's first page) as a Blob; 404 when it has none.
  async portalAttachmentThumbBlob(key, attachmentId) {
    return (await client.get(`/api/portal/requests/${enc(key)}/attachments/${enc(attachmentId)}/thumb`,
      { responseType: 'blob' })).data;
  },
  // The portal's own look — one for the whole help centre (2026-10-11).
  async portalGet() {
    return (await client.get('/api/support-desk/portal')).data;
  },
  // PATCH-shaped { headline?, intro?, bannerUrl? } — admin only.
  async portalSave(patch) {
    return (await client.put('/api/support-desk/portal', patch)).data;
  },
  // How this desk looks on the portal — admin only. (Since 2026-10-11 the
  // portal shows its own look; kept for the API.)
  async portalLookSave(spaceId, patch) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/portal`, patch)).data;
  },

  // { statuses: [{ name, label }] } — every status a customer of this desk
  // could be shown, and what they are currently told it means.
  async statusList(spaceId) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}/statuses`)).data;
  },
  async statusSave(spaceId, labels) {
    return (await client.put(`/api/support-desk/projects/${enc(spaceId)}/statuses`, { labels })).data;
  },

  // { request: null } | { request: { key, spaceId, requestTypeId, requestTypeName } }
  // — is this work item a portal request, and which form raised it. Answers
  // for every key; null is "not a portal request", not a failure.
  async requestForWorkItem(issueKey) {
    return (await client.get(`/api/support-desk/work-items/by-key/${enc(issueKey)}`)).data;
  },

  // --- cloned requests ---

  // What is waiting to be linked: the forms the imported tickets came in on,
  // how many tickets each accounts for, and the form here that probably
  // matches. Admin only.
  async clonedRequests(spaceId) {
    return (await client.get(`/api/support-desk/projects/${enc(spaceId)}/cloned-requests`)).data;
  },
  // { mapping: { '<group key>': '<request type id>' } } — only the rows you
  // send are acted on, so "leave alone" is simply an absent key.
  async clonedRequestsLink(spaceId, mapping) {
    return (await client.post(`/api/support-desk/projects/${enc(spaceId)}/cloned-requests`, { mapping })).data;
  },
  // Removes only what a linking wrote. A request raised on the portal keeps
  // its own row.
  async clonedRequestsUndo(spaceId) {
    return (await client.delete(`/api/support-desk/projects/${enc(spaceId)}/cloned-requests`)).data;
  },

  // The work types this PROJECT uses — a form can only create one of
  // these, whatever else the site has. Addressed by project key, which is
  // what this endpoint takes.
  async projectWorkTypes(spaceKey) {
    return (await client.get(`/api/project/${enc(spaceKey)}/issuetypes`)).data;
  },
  // { fields: [{ fieldId, name, required, type }] } — what this work
  // type's CREATE SCREEN offers, which is all a form may ever show.
  async screenFields(spaceId, workTypeId) {
    return (await client.get(
      `/api/support-desk/projects/${enc(spaceId)}/work-types/${enc(workTypeId)}/fields`)).data;
  },
};

export default supportDeskApi;
